import styles from 'components/cases/common/Interview/Interview.module.scss'
import TitleFlex from 'components/cases/common/TitleFlex'
import { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import InterviewContents from 'components/cases/common/Interview/InterviewContents'
import { ContentData } from 'components/cases/common/Interview/fragments/type'
gsap.registerPlugin(ScrollTrigger)

const sectionTitle: { title: string; sub_title: string } = {
  title: 'Interview',
  sub_title: 'インタビュー',
}

const contentData: ContentData[][] = [
  [
    {
      type: 'sectionTitle',
      text: '生成AI時代だからこそ、デザインシステムが活きる',
    },
    {
      type: 'textBox',
      texts: ['―― デザインシステム構築に取り組む前、KANNAの開発現場ではどのような課題を感じていましたか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '小野田さん',
      texts: [
        <>
          <p>
            世の中がChatGPTなどの生成AIでにぎわうなか、開発サイドではAIを実装に取り入れ始めていました。今後、AIによってエンジニアの開発スピードが上がると、「デザイン待ち」が起きるのではないかという話が上司からあり、デザインへのAI活用を調査するなかで繰り返し出てきたキーワードが「デザインシステム」だったんです。改めて足元を見ると、コンポーネントの使い方が人によってバラバラ。そもそもFigmaのデザインデータと実装に差異があって、どちらを「正」とすべきか、日々悩むシーンが多かったんです。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/aldagram/interview01.webp',
      alt: 'デザインシステム導入前後のイメージ',
      text: 'デザインシステム導入前後のイメージ',
    },
    {
      type: 'greyBox',
      name: '今町さん',
      texts: [
        <>
          <p>
            同じKANNAのなかでも、機能ごとに微妙にデザインのズレがあって、「どっちが正しいものですか？」というやり取りが発生していました。デザインの正解がないゆえのコミュニケーションコストです。ボタンの大きさや色、余白まで微妙に違うと、コード側も個別最適化されて余計なコンポーネントが増えていく。プロダクトが成長するほど複雑化するので、開発のスピードを落とさない形で統一・整理できないかと考えていました。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― 数あるアプローチのなかで、特に「デザインシステム」にフォーカスした理由は？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '小野田さん',
      texts: [
        <>
          <p>
            AIに何かを作らせるにはルールがないと使い物にならず、そのルールを突き詰めていくと、デザインシステムに行き着きました。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '今町さん',
      texts: [
        <>
          <p>
            たとえば Figma Variables
            を使わないと、カラーコードがハードコードされてしまう。またそれとは別に、デザイン側でパーツごとにコンポーネント化ができていないと、コード側でAIが「車輪の再発明」のように重複したコンポーネントを生成してしまうことがある。デザイン側でトークン化・コンポーネント化されていることが、生成AIに適切に仕事をさせる前提だと考えました。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: [
        '―― デザインシステムの整備を外部に相談しようと思ったきっかけと、Aitriaを選ばれた決め手を教えてください。',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '五十嵐さん',
      texts: [
        <>
          <p>
            最初は外部に相談できるものだと思っておらず、デザインシステムの基礎であるデザイントークンだけをチーム内で進めようとしていました。ただ、当時のCTOから「デザインシステムはプロダクトの土台で必須である。かつ、作るなら一気に作るべきだ」という話があり、外部の力を借りる方針になりました。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '今町さん',
      texts: [
        <>
          <p>
            社内にデザインシステムを作った経験者がおらず、ゼロから自分たちだけで作るのは「これが本当にベストプラクティスなのか」という不安がある。ナレッジを持つパートナーと組めば、王道かつ現実的なものを作れるというのが大きな理由です。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '清水さん',
      texts: [
        <>
          <p>
            Aitriaさんにお願いした決め手は、メールのやり取り一つを取っても、向き合ってくれている感じがあったこと。細かいスケジューリングまで提案資料からイメージできましたし、こちらの意向に沿ったリカバリープランもすぐ出てきて、信頼できると感じました。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '今町さん',
      texts: [
        <>
          <p>
            エンジニアから見ると、一番バランスが取れていました。デザインは強くてもエンジニアが在籍していない会社だと、技術面のサポートが受けられない。Aitriaさんはデザイナーもエンジニアもいて、両面で支援していただける。そこが大きかったです。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: 'Aitria 今野',
      texts: [
        <>
          <p>
            デザインと技術の両面を評価いただけたことは、私たちとしても非常にうれしく思っています。プロジェクトでは、デザイナーとエンジニアが参加するQ&Aの場を設け、デザインシステムとしての考え方だけでなく、実装時に生じる疑問や技術的な実現性についても、一つひとつ確認しながら進めました。デザインと実装を分けて考えるのではなく、双方の視点をつなぎ、アルダグラムの皆さまが納得感を持って判断できるよう支援することを大切にしていました。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/aldagram/interview02.webp',
      alt: 'デザインシステムのイメージ',
      text: 'デザインシステムのイメージ',
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '「枠組み」を受け取り、中身は自社チームで作る',
    },
    {
      type: 'textBox',
      texts: [
        '―― 一般的にデザインシステム構築となると、要件定義から開発、ガイドラインの作成までをベンダーが担うケースが多いと思いますが、今回は、Aitriaがテンプレートを提供し、御社が内製してレビューを受けるという特徴的な進め方となりました。実際の手応えはいかがでしたか？',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '五十嵐さん',
      texts: [
        <>
          <p>作業は大変でしたが、「この順番でこれをやればいい」という計画があるので不安はあまりありませんでした。</p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '松木さん',
      texts: [
        <>
          <p>
            全部やっていただくと中身が分からないまま使うことになるし、逆にゼロから自分たちだけで進めると迷いも無駄も多い。そのちょうど真ん中で、布石を置いてもらい、議論に集中できました。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '清水さん',
      texts: [
        <>
          <p>
            腹落ちした状態で成果物にたどり着けましたし、自分たちで作ったことで「自分ごと」になり、運用への移行もスムーズでした。最終的なメリットは大きかったと思っています。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: 'Aitria 今野',
      texts: [
        <>
          <p>
            Aitriaとしてもこのやり方は初めてで、どこまでお伝えすべきか迷いはありました。お渡ししたのはデザインシステムそのものではなく、「作る枠組み」。いわば「ここを埋めていけば基本的なことは決められる」という要件定義書のテンプレートです。
            <br />
            やろうと思うと膨大になってしまうので、「まずはこのあたりから」というベースを示し、中身の議論はすべて皆さんにやっていただきました。考えるべきところが明確なので、今何をすればよいのか分からない、という状態は避けられたのではないかと思っています。
          </p>
        </>,
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '議論を重ねて見えた「KANNAらしさ」',
    },
    {
      type: 'textBox',
      texts: ['―― 社内で議論を重ねるなかで、大変だったことや発見はありましたか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '清水さん',
      texts: [
        <>
          <p>
            いざ見ていくと、実装されているデザインごとの「揺れ」がすごく多くて、並べて一つの答えを出すのが大変でした。デザイナー間でも状況に応じた用途の認識が意外と違っていて、話し始めると時間がどんどん消えていく。
            <br />
            <br />
            コンポーネント自体の話と使い方の話が混在しがちだったのを、「使い方はデザインガイドラインで定義しよう」とAitriaの今野さんが都度切り分けてくださってからは、頭の整理ができるようになりました。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '影山さん',
      texts: [
        <>
          <p>
            マイルストーンはもちろん、「デザインシステムとしてはこうあるべき」という客観的な意見を定例ミーティングのたびにいただけたのは、すごく安心感がありました。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― 定例ミーティングを含め、Aitriaからのサポートで印象的だったものはありますか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '五十嵐さん',
      texts: [
        <>
          <p>
            プロジェクトが進むにつれて時間が足りなくなっていった時に、合間に「分科会」を提案して開いてもらえたのがありがたかったですね。
            <br />
            スケジュールが遅れたときは「いま、結構遅れていますよ」と鋭く指摘しつつ、リカバリープランをすぐ出してくださる。それから、こちらの質問に対してめちゃくちゃ調べてくださる。回答がいつもボリューミーでした（笑）。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: 'Aitria 大林',
      texts: [
        <>
          <p>
            今回のプロジェクトでは、私自身は手を動かして成果物を出す立場ではないので、デザイナーとして知識の面で貢献したくて。コンポーネントやFigmaの使い方について、「このパターンならこういうメリットがあります」と、自分の経験も交えてなるべく細かくお伝えするよう心がけていました。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― 「KANNAらしさ」を言語化するプロセスは、どのような体験でしたか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '小野田さん',
      texts: [
        <>
          <p>
            ワークショップ形式でやったのですが、デザイナー・エンジニアだけでなく、PdMやプロダクトオーナー、エンジニアマネージャーなど、KANNA全体を把握する役割の人たちにも入ってもらい、まず目線合わせができたのが良かったです。
            <br />
            そのうえで、普段思っていたことが整理されて重要なポイントに集約された、という感覚でした。これまで「思っていたこと」を見える化し、これからのKANNAを考えていく代表メンバーとすり合わせられた。デザイナーの中だけでなく、プロジェクト全体で意思疎通ができたのが良かったと思います。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/aldagram/interview03.webp',
      alt: '実際のワークショップ風景',
      text: '実際のワークショップ風景',
    },
    {
      type: 'textBox',
      texts: [
        '―― デザイナー4名が日常業務とデザインシステム構築を兼務するなかで、体制の工夫や、想定と違ったことはありましたか？',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '清水さん',
      texts: [
        <>
          <p>
            私がデザインチームのリソースを見ているのですが、当初は「デザインシステムに関する業務は全体の稼働の20％で進めよう」という企画でした。実際にやってみると、スケジュールからどんどん遅れていく。途中からデザインシステム以外の業務をセーブして、一気に進めました。分量を減らすより、「一気に投入できる状態」をつくることが必要だったなと。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '五十嵐さん',
      texts: [
        <>
          <p>
            コンポーネントの作り込みに想定以上に時間がかかりましたね。しかも作ったら完成、ではなく、「節目」でしかない。実際の開発で使ってみないと直すべき箇所が分からない、継続的に更新していくものなんだと分かりました。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: 'Aitria 大林',
      texts: [
        <>
          <p>
            デザインシステムは組織に根付かないと形骸化してしまいます。多くの企業様での取り組みを見てきましたが、実際の現場で使いやすいよう絶えずアップデートしていくこと、そして社内へ浸透させるための働きかけを積極的に行うことがとても重要だと考えています。
          </p>
        </>,
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '迷いが減り、AIとの協働へ',
    },
    {
      type: 'textBox',
      texts: ['―― デザインシステムができて、日々の業務で最も変わったと感じることは？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '清水さん',
      texts: [
        <>
          <p>
            使い始めるのは来月からなので、大きな変化が表れるのはこれから、というのが正直なところですが…これまでは「本当にこれでいいのかな」と思いながらコンポーネントを作っていたのが、土台ができて、自信を持って迷いなく画面を組んでいける。その「迷っていた時間」をAI生成の検証や効率化に使えそうで、楽しみです。例えば、使う色の候補が絞られたので、かなり選びやすくなりました。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '今町さん',
      texts: [
        <>
          <p>
            お恥ずかしい話ですが、類似のコンポーネントが乱立しており、生成AIでコーディングが加速するほどこの状態がさらに悪化するという危機感がありました。今回のプロジェクトで整理の土台ができつつあり、これを軸に共通化を進めていけば、生産性をさらに上げていけるフェーズに入れると考えています。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '影山さん',
      texts: [
        <>
          <p>
            まさにいま検証しているのが、AIエージェントを使ってFigma
            Variablesを半自動でコードに落とし込む仕組みです。Variablesのエクスポート機能で出力したJSONはAIも読み取りやすく、デザインの差分をすぐコードに反映できる仕組みが整いつつあります。こうした定義がないと、毎回ゼロから「コンポーネントを使うべきか、新規に作るべきか」の判断材料をAIに渡さないといけない。デザインシステムは、デザイナーと開発だけでなく、開発とAIエージェントとのコミュニケーションも円滑にしてくれるものだと感じています。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― 最後に、今後のKANNAやデザインシステムの展開、チームの目指す方向性を教えてください。'],
      wide: false,
      bold: true,
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/aldagram/interview04.webp',
      alt: '今後のKANNAが目指す方向性',
      text: '今後のKANNAが目指す方向性',
    },
    {
      type: 'greyBox',
      name: '小野田さん',
      texts: [
        <>
          <p>
            まずはKANNAのWebを中心に、ちゃんと運用できる形を今後3か月で作りたい。その後、アプリ側をどうしていくかも検討したいです。チームとしては、デザインシステムをもとにAIを使ってたたき台となる初期のモックアップを生成し、それがユーザーにとっての使いやすさや、ブランドに合っているかを私たちが評価する、という状態を目指したいと思っています。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '今町さん',
      texts: [
        <>
          <p>
            コーディングが速くなった一方で、ボトルネックが企画やデザインの段階に移ってきています。いまは開発側が先にプロトタイプを作り、デザイナーとすり合わせながら進める並行プロセスをトライアル中です。そこで、今回作ったデザインガイドラインを「KANNAらしいデザインとは何か」を示すコンテキストとしてAIに読み込ませることで、クオリティの高いデザインを作れるのではないかと考えています。今回のデザインシステムを軸に、開発のやり方そのものを模索していくことになりそうです。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: 'Aitria 今野',
      texts: [
        <>
          <p>
            いまはベースを作っていただいた段階なので、今後もコンポーネントを増やしたり、改善したりする作業が続いていくと思います。そして、やはりAIがキーワードになってくるはずです。Figmaなどのデザインツールを使用せずに、生成AIツールを使用して、コードベースでデザインをするようになっている流れも出てくると思われるので、そこに合わせてデザインシステム自体をアップデートしていけば、業界的にも新しい取り組みになるかもしれません。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '小野田さん',
      texts: [
        <>
          <p>
            今回作ったデザインシステムの本格的な運用が始まった後、どのように開発の現場が変わっていくのか楽しみです。
            <br />
            ブランドとしての一貫性を保ったまま、開発の効率をいかに上げられるか、という点に期待しています。
            <br />
            デザインシステムを土台として、AIと人間が適切な役割分担をすることで、KANNAを世界中のユーザーに選ばれるプロダクトに育てていきたいですね。
          </p>
        </>,
      ],
    },
  ],
]

const tweenVars: gsap.TimelineVars = {
  y: 50,
  duration: 1,
  opacity: 0,
  ease: Power4.easeOut,
}

const Interview: React.FC = () => {
  const timeline = gsap.timeline()
  const wrapper = useRef<HTMLElement>()
  const text_container = useRef<HTMLDivElement>()

  useEffect(() => {
    timeline.from(text_container.current, tweenVars, 0.4)

    ScrollTrigger.create({
      animation: timeline,
      trigger: wrapper.current,
      start: 'top-=100 90%',
      toggleActions: 'play none none play',
    })
  }, [])

  return (
    <section className={styles.wrapper} ref={wrapper}>
      <div className={styles.content}>
        <TitleFlex sectionTitle={sectionTitle} />
        <div className={styles.article_container} ref={text_container}>
          <InterviewContents contentData={contentData} />
        </div>
      </div>
    </section>
  )
}

export default Interview
